import { t } from "@/lib/i18n/interpolate";
import {
  capitalizeFirst,
  stripEnd,
  tidy,
  tidySeams,
} from "@/lib/tool-kit/essay/sentence";

export type ThesisType = "argumentative" | "analytical" | "expository";

export interface ThesisInput {
  claim: string;
  /** Up to three supporting reasons, written as noun phrases. */
  reasons: string[];
  topic: string;
}

export interface ThesisFrames {
  /** Uses `{claim}`, `{reasons}`. */
  argumentative: string;
  /** Uses `{topic}`, `{reasons}`, `{claim}`. */
  analytical: string;
  /** Uses `{topic}`, `{reasons}`. */
  expository: string;
}

export interface ThesisPlaceholders {
  claim: string;
  /** Uses `{number}`. */
  reason: string;
  topic: string;
}

const REASON_COUNT = 3;

function listOf(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, {
      style: "long",
      type: "conjunction",
    }).format(items);
  } catch {
    return items.join(", ");
  }
}

/**
 * Fills the three thesis frames. Every frame takes the reasons as noun phrases ("sleep
 * lost to early classes") and the claim as a clause ("high schools should start
 * later"), so one set of reasons reads correctly in all three versions. A blank box shows
 * a bracketed placeholder so the frame is visible before the writer commits.
 */
export function buildTheses(
  input: ThesisInput,
  frames: ThesisFrames,
  placeholders: ThesisPlaceholders,
  locale: string
): Record<ThesisType, string> {
  // The writer's own capitals are kept exactly as typed: a frame never lowercases a
  // field, so "Mars" and "Congress" survive. Only the topic that opens the expository
  // sentence is capitalised.
  const clause = (text: string) => stripEnd(tidy(text));
  const field = (text: string, placeholder: string) =>
    text.trim() === "" ? placeholder : clause(text);

  const topic = field(input.topic, placeholders.topic);
  const claim = field(input.claim, placeholders.claim);
  const reasons = listOf(
    Array.from({ length: REASON_COUNT }, (_, index) =>
      field(
        input.reasons[index] ?? "",
        t(placeholders.reason, { number: index + 1 })
      )
    ),
    locale
  );
  // The expository sentence opens with the topic, so a typed topic gets its capital back.
  const topicOpening =
    input.topic.trim() === "" ? topic : capitalizeFirst(topic);

  return {
    argumentative: tidySeams(t(frames.argumentative, { claim, reasons })),
    analytical: tidySeams(t(frames.analytical, { topic, claim, reasons })),
    expository: tidySeams(
      t(frames.expository, { topic: topicOpening, reasons })
    ),
  };
}
